import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users as UsersIcon, 
  Briefcase, 
  Building2, 
  Package, 
  ShoppingCart, 
  Calculator,
  Settings,
  LogOut,
  Home
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import NotificationPanel from './NotificationPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PanelHeader = ({ 
  title, 
  titleIcon: TitleIcon, 
  titleColor = 'text-gray-800',
  logoSrc = null,
  children 
}) => {
  const { user, logout } = useAuth();
  const [unreadCounts, setUnreadCounts] = useState({});

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  const panelAccess = user?.panel_access || [];
  const userRole = user?.role;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-[hsl(var(--border))] bg-white/95 backdrop-blur">
      <div className="flex h-16 items-center justify-between px-4">
        {/* Left: Logo/Title */}
        <div className="flex items-center gap-3">
          {logoSrc ? (
            <img 
              src={logoSrc} 
              alt="Logo" 
              className="h-[85px] w-auto"
            />
          ) : TitleIcon ? (
            <div className="flex items-center gap-2">
              <TitleIcon className={`h-8 w-8 ${titleColor}`} />
              <span className={`text-xl font-bold ${titleColor}`}>{title}</span>
            </div>
          ) : (
            <span className={`text-xl font-bold ${titleColor}`}>{title}</span>
          )}
        </div>
        
        {/* Center: Panel Title (optional - can be passed as children) */}
        <div className="flex-1 flex justify-center">
          {children}
        </div>
        
        {/* Right: User name, Panel buttons, Logout */}
        <div className="flex items-center gap-2">
          <Link to="/dashboard/my-info">
            <span className="text-sm font-medium cursor-pointer hover:text-[hsl(var(--brand-700))] transition-colors mr-2">
              {user?.name}
            </span>
          </Link>

          {/* Admin Panel */}
          {(panelAccess.includes('admin') || userRole === 'admin') && (
            <Link to="/admin/users">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs" data-testid="header-admin-panel-btn">
                <Settings className="h-3 w-3" />
                Admin
              </Button>
            </Link>
          )}

          {/* HR Panel */}
          {(panelAccess.includes('hr') || userRole === 'hr_manager') && (
            <Link to="/hr/employees">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs relative" data-testid="header-hr-panel-btn">
                <UsersIcon className="h-3 w-3" />
                İK
                {(unreadCounts.hr_inbox > 0 || unreadCounts.hr_management_rejected > 0) && (
                  <Badge variant="destructive" className="absolute -top-2 -right-2 h-4 min-w-4 p-0 flex items-center justify-center text-[10px]">
                    {(unreadCounts.hr_inbox || 0) + (unreadCounts.hr_management_rejected || 0)}
                  </Badge>
                )}
              </Button>
            </Link>
          )}

          {/* Work Panel */}
          {panelAccess.includes('work') && (
            <Link to="/work/dashboard">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="header-work-panel-btn">
                <Briefcase className="h-3 w-3" />
                İş
              </Button>
            </Link>
          )}

          {/* Branch Panel */}
          {panelAccess.includes('branch') && (
            <Link to="/branch/dashboard">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 relative" data-testid="header-branch-panel-btn">
                <Building2 className="h-3 w-3" />
                Şube
                {(unreadCounts.branch_inbox > 0 || unreadCounts.branch_hr_rejected > 0 || unreadCounts.branch_management_rejected > 0) && (
                  <Badge variant="destructive" className="absolute -top-2 -right-2 h-4 min-w-4 p-0 flex items-center justify-center text-[10px]">
                    {(unreadCounts.branch_inbox || 0) + (unreadCounts.branch_hr_rejected || 0) + (unreadCounts.branch_management_rejected || 0)}
                  </Badge>
                )}
              </Button>
            </Link>
          )}

          {/* Board Panel */}
          {(panelAccess.includes('board') || userRole === 'yonetim_kurulu') && (
            <Link to="/board/dashboard">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-[hsl(var(--brand-400))] text-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-50))] relative" data-testid="header-board-panel-btn">
                <Building2 className="h-3 w-3" />
                YK
                {unreadCounts.board_pending > 0 && (
                  <Badge variant="destructive" className="absolute -top-2 -right-2 h-4 min-w-4 p-0 flex items-center justify-center text-[10px]">
                    {unreadCounts.board_pending}
                  </Badge>
                )}
              </Button>
            </Link>
          )}

          {/* Depot Panel */}
          {(panelAccess.includes('depo') || panelAccess.includes('depot')) && (
            <Link to="/depot/entry">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="header-depot-panel-btn">
                <Package className="h-3 w-3" />
                Depo
              </Button>
            </Link>
          )}

          {/* Purchasing Panel */}
          {panelAccess.includes('purchasing') && (
            <Link to="/purchasing/requests">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="header-purchasing-panel-btn">
                <ShoppingCart className="h-3 w-3" />
                Satın Alma
              </Button>
            </Link>
          )}

          {/* Accounting Panel */}
          {panelAccess.includes('accounting') && (
            <Link to="/accounting/income">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="header-accounting-panel-btn">
                <Calculator className="h-3 w-3" />
                Muhasebe
              </Button>
            </Link>
          )}

          {/* Bildirim Çanı (tüm panellerde) */}
          <NotificationPanel />

          {/* Home Button */}
          <Link to="/dashboard/messages">
            <Button variant="outline" size="sm" className="gap-1 px-2" data-testid="header-home-btn">
              <Home className="h-4 w-4" />
            </Button>
          </Link>
          
          {/* Logout Button */}
          <Button variant="ghost" size="sm" onClick={logout} data-testid="header-logout-btn">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default PanelHeader;
